import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, TrendingUp, Calendar, Gift, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

export default function ReferralsTab() {
  const [referralStats, setReferralStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedTokens, setExpandedTokens] = useState(new Set());
  const [totalReferrals, setTotalReferrals] = useState(0);

  useEffect(() => {
    fetchReferralStats();
  }, [selectedMonth, selectedYear]);

  const fetchReferralStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        month: selectedMonth.toString(),
        year: selectedYear.toString()
      });
      
      const response = await api.get(`/admin/referrals?${params.toString()}`);
      const stats = response.data.referral_stats || [];
      setReferralStats(stats);
      
      // Calculate total referrals
      const total = stats.reduce((sum, stat) => sum + stat.total_referrals, 0);
      setTotalReferrals(total);
    } catch (error) {
      console.error('Failed to load referral stats:', error);
      toast.error('Failed to load referral statistics');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (token) => {
    const newExpanded = new Set(expandedTokens);
    if (newExpanded.has(token)) {
      newExpanded.delete(token);
    } else {
      newExpanded.add(token);
    }
    setExpandedTokens(newExpanded);
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="bg-gradient-to-br from-emerald-50 to-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-emerald-600" />
            Referral Program Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">Total Referrals This Month</p>
              <p className="text-3xl font-bold text-emerald-600">{totalReferrals}</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">Active Referrers</p>
              <p className="text-3xl font-bold text-blue-600">{referralStats.length}</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">Avg. Referrals per User</p>
              <p className="text-3xl font-bold text-purple-600">
                {referralStats.length > 0 ? (totalReferrals / referralStats.length).toFixed(1) : '0'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Filter by Date
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {months.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral Stats Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Referral Details - {months[selectedMonth - 1]} {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading referral statistics...</div>
          ) : referralStats.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No referrals found for this period</p>
            </div>
          ) : (
            <div className="space-y-3">
              {referralStats.map((stat) => (
                <div key={stat.referral_token} className="border rounded-lg overflow-hidden">
                  {/* Referrer Header */}
                  <div 
                    className="bg-gray-50 p-4 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleExpanded(stat.referral_token)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="bg-emerald-100 rounded-full p-2">
                          <Users className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{stat.referrer_name}</p>
                          <p className="text-sm text-gray-500">{stat.referrer_email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <Badge className="bg-emerald-500 text-white">
                          {stat.referral_token}
                        </Badge>
                        <p className="text-sm text-gray-600 mt-1">
                          {stat.total_referrals} {stat.total_referrals === 1 ? 'referral' : 'referrals'}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm">
                        {expandedTokens.has(stat.referral_token) ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Referrals List */}
                  {expandedTokens.has(stat.referral_token) && (
                    <div className="bg-white p-4 border-t">
                      <h4 className="font-medium text-gray-700 mb-3">Referred Users:</h4>
                      <div className="space-y-2">
                        {stat.referrals && stat.referrals.length > 0 ? (
                          stat.referrals.map((referral, index) => (
                            <div 
                              key={referral.id || index} 
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                            >
                              <div>
                                <p className="font-medium text-gray-900">{referral.full_name}</p>
                                <p className="text-sm text-gray-500">{referral.email}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">
                                  Joined: {new Date(referral.referral_date).toLocaleDateString()}
                                </p>
                                <Badge 
                                  variant={referral.subscription_status === 'active' ? 'default' : 'secondary'}
                                  className={referral.subscription_status === 'active' ? 'bg-emerald-500' : ''}
                                >
                                  {referral.subscription_status}
                                </Badge>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">No detailed referral data available</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
